import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { StatusPill } from "../components/UI";
import { useAuth } from "../lib/auth";

const ROLE_TONE = { "Director": "maroon", "Office": "blue", "Accounts": "green", "Read-only": "neutral" };
const ROLE_DESCRIPTIONS = {
  "Director": "Can do anything — approve month-end, connect integrations, add users, edit machines, everything.",
  "Office": "Day-to-day operations. Can start hires, edit hires, take payments, run month-end. Can't manage users or change integrations.",
  "Accounts": "The money side — invoicing, month-end, rate changes, customer accounts, Xero. Can't edit machines/compliance or manage users.",
  "Read-only": "Can view everything but can't change anything. Good for accountants or auditors.",
};
const ROLE_DESC = {
  "Director": "Can do anything — approve month-end, connect integrations, add or remove people, change rates, edit templates.",
  "Office": "Can start and off-hire, add customers, approve month-end, send reminders. Can't touch integrations or people.",
  "Accounts": "The money side — invoicing, month-end, rate changes, customer accounts, Xero. Can't start hires, edit machines/compliance, manage users or change integrations.",
  "Read-only": "Can view everything but can't change anything. Good for accountants or auditors.",
};

function DashboardVisibilityCard() {
  const [state, setState] = useState(null); // {sections, roles, visibility}
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/settings/dashboard-visibility")
      .then(r => setState(r.data))
      .catch(e => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };
  useEffect(load, []);

  const flip = async (role, section, visible) => {
    setSaving(true); setErr("");
    // Optimistic update — snappier UI, revert on failure.
    setState(prev => ({
      ...prev,
      visibility: {
        ...prev.visibility,
        [role]: { ...prev.visibility[role], [section]: visible },
      },
    }));
    try {
      await api.put("/settings/dashboard-visibility", { role, section, visible });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      load(); // revert to server truth
    } finally { setSaving(false); }
  };

  if (!state) {
    return (
      <div className="ach-card" style={{marginBottom:16}} data-testid="dashboard-vis-card">
        <h2 className="ach-card__title">What each role sees on the Dashboard</h2>
        <div style={{fontSize:12, color:"var(--ach-text-2)"}}>Loading…</div>
      </div>
    );
  }

  return (
    <div className="ach-card" style={{marginBottom:16}} data-testid="dashboard-vis-card">
      <h2 className="ach-card__title">What each role sees on the Dashboard</h2>
      <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:2, marginBottom:12, lineHeight:1.5}}>
        Directors always see everything. For everyone else you can hide the revenue-facing bits
        so the day-to-day operational view isn&apos;t cluttered with money figures they don&apos;t need.
        The revenue-adjacent rows are marked with $ and hidden by default.
      </div>
      {err && <div style={{color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12, marginBottom:10}}>{err}</div>}
      <div style={{overflowX:"auto"}}>
      <table className="ach-table" data-testid="dashboard-vis-grid">
        <thead>
          <tr>
            <th style={{width:"55%"}}>Dashboard section</th>
            {state.roles.map(r => (
              <th key={r} style={{textAlign:"center"}}>{r}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {state.sections.map(sec => (
            <tr key={sec.key} data-testid={`vis-row-${sec.key}`}>
              <td>
                {sec.label}
                {sec.revenue && <span style={{color:"var(--ach-text-2)", fontSize:11, marginLeft:6}}>· $</span>}
              </td>
              {state.roles.map(r => {
                const on = !!state.visibility[r]?.[sec.key];
                return (
                  <td key={r} style={{textAlign:"center"}}>
                    <label style={{display:"inline-flex", alignItems:"center", gap:6, cursor: saving ? "wait" : "pointer", fontSize:12}}>
                      <input type="checkbox" checked={on} disabled={saving}
                             onChange={()=>flip(r, sec.key, !on)}
                             data-testid={`vis-toggle-${r}-${sec.key}`}
                             style={{width:16, height:16, cursor:"inherit"}} />
                      <span style={{color: on ? "var(--ach-green)" : "var(--ach-text-2)"}}>{on ? "Shown" : "Hidden"}</span>
                    </label>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div style={{fontSize:11, color:"var(--ach-text-2)", marginTop:10, lineHeight:1.5}}>
        Changes take effect the next time that user refreshes the Dashboard. Hidden
        values aren&apos;t just visually removed — the backend strips them from the
        API response, so nothing leaks in browser dev tools either.
      </div>
    </div>
  );
}


function RoleChooser({ value, onChange, disabled }) {
  return (
    <div style={{display:"grid", gap:8}}>
      {Object.keys(ROLE_DESC).map(r => (
        <label key={r} data-testid={`role-choice-${r}`}
          style={{display:"flex", alignItems:"flex-start", gap:10, padding:"10px 12px", border:`1px solid var(--ach-card-border)`, borderRadius:8,
            background: value === r ? "var(--ach-page-bg)" : "#fff", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1}}>
          <input type="radio" name="role" value={r} checked={value === r} disabled={disabled}
            onChange={()=>onChange(r)} style={{marginTop:4}} />
          <div>
            <div style={{fontSize:13, fontWeight:600, color:"var(--ach-charcoal)"}}>{r}</div>
            <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:2, lineHeight:1.4}}>{ROLE_DESC[r]}</div>
          </div>
        </label>
      ))}
    </div>
  );
}

function AddPersonDialog({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Office");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/settings/users", { name, email, role });
      setResult(r.data);
      onSaved();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="ach-modal-backdrop" data-testid="add-person-dialog" onClick={onClose}>
      <div className="ach-modal" style={{maxWidth: 560}} onClick={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header">
          <h2 style={{margin:0,fontSize:16,fontWeight:600}}>{result ? "Account created" : "Add a person"}</h2>
          <button className="ach-btn ach-btn--grey" onClick={onClose} style={{padding:"6px 10px"}} data-testid="add-person-close">Close</button>
        </div>
        <div className="ach-modal__body">
          {result ? (
            <div>
              <div style={{fontSize:13, color:"var(--ach-text-1)", marginBottom:12}}>{result.message}</div>
              <div style={{background:"var(--ach-page-bg)", padding:12, borderRadius:8, border:"1px dashed var(--ach-card-border)"}}>
                <div style={{fontSize:11, textTransform:"uppercase", letterSpacing:0.6, color:"var(--ach-text-2)"}}>Temporary password</div>
                <div style={{fontFamily:"monospace", fontSize:18, fontWeight:700, color:"var(--ach-maroon)", marginTop:4}} data-testid="add-person-temp-pw">{result.temp_password}</div>
                <div style={{fontSize:11, color:"var(--ach-text-2)", marginTop:6}}>Write this down or copy it now — it&apos;s only shown once. Hand it to the person directly.</div>
              </div>
            </div>
          ) : (
            <div className="ach-form-grid">
              <div className="ach-field" style={{gridColumn:"1 / -1"}}>
                <label>Their name <span style={{color:"var(--ach-red)"}}>*</span></label>
                <input data-testid="add-person-name" value={name} onChange={(e)=>setName(e.target.value)} placeholder="e.g. Sarah Wilson" />
              </div>
              <div className="ach-field" style={{gridColumn:"1 / -1"}}>
                <label>Email they sign in with <span style={{color:"var(--ach-red)"}}>*</span></label>
                <input data-testid="add-person-email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="sarah@allcranes.com.au" />
              </div>
              <div className="ach-field" style={{gridColumn:"1 / -1"}}>
                <label>What they&apos;ll be able to do <span style={{color:"var(--ach-red)"}}>*</span></label>
                <RoleChooser value={role} onChange={setRole} />
              </div>
              {err && <div style={{gridColumn:"1 / -1", color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12}}>{err}</div>}
            </div>
          )}
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          {result ? (
            <button className="ach-btn ach-btn--primary" onClick={onClose} data-testid="add-person-done">Done</button>
          ) : (
            <button className="ach-btn ach-btn--primary" onClick={submit} disabled={busy || !name || !email} data-testid="add-person-submit">
              {busy ? "Saving…" : "Create account →"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function EditPersonDialog({ target, currentUser, onClose, onSaved }) {
  const [role, setRole] = useState(target.role);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isSelf = target.id === currentUser.id;

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/settings/users/${target.id}`, { role });
      onSaved(); onClose();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); setBusy(false); }
  };

  return (
    <div className="ach-modal-backdrop" data-testid="edit-person-dialog" onClick={onClose}>
      <div className="ach-modal" style={{maxWidth: 560}} onClick={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header">
          <h2 style={{margin:0,fontSize:16,fontWeight:600}}>Change what {target.name} can do</h2>
        </div>
        <div className="ach-modal__body">
          {isSelf && (
            <div style={{padding:10, background:"var(--ach-amber-tint)", color:"var(--ach-amber)", borderRadius:6, fontSize:12, marginBottom:12}}>
              You can&apos;t change your own role — ask the other Director to do it.
            </div>
          )}
          <RoleChooser value={role} onChange={setRole} disabled={isSelf} />
          {err && <div style={{color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12, marginTop:10}}>{err}</div>}
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={onClose} data-testid="edit-person-cancel">Cancel</button>
          <button className="ach-btn ach-btn--primary" onClick={save} disabled={busy || isSelf || role === target.role} data-testid="edit-person-save">
            {busy ? "Saving…" : "Save →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmActionDialog({ title, body, confirmLabel, danger, onCancel, onConfirm, testid }) {
  return (
    <div className="ach-modal-backdrop" data-testid={testid} onClick={onCancel}>
      <div className="ach-modal" style={{maxWidth: 460}} onClick={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header"><h2 style={{margin:0,fontSize:16,fontWeight:600}}>{title}</h2></div>
        <div className="ach-modal__body">
          <p style={{margin:0, fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>{body}</p>
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={onCancel} data-testid={`${testid}-cancel`}>Cancel</button>
          <button className={`ach-btn ${danger ? "ach-btn--primary" : "ach-btn--primary"}`}
            style={danger ? {background:"var(--ach-red)"} : undefined}
            onClick={onConfirm} data-testid={`${testid}-go`}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordResultDialog({ result, onClose }) {
  return (
    <div className="ach-modal-backdrop" data-testid="reset-pw-result" onClick={onClose}>
      <div className="ach-modal" style={{maxWidth: 460}} onClick={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header"><h2 style={{margin:0,fontSize:16,fontWeight:600}}>Password reset</h2></div>
        <div className="ach-modal__body">
          <div style={{fontSize:13, color:"var(--ach-text-1)", marginBottom:12}}>{result.message}</div>
          <div style={{background:"var(--ach-page-bg)", padding:12, borderRadius:8, border:"1px dashed var(--ach-card-border)"}}>
            <div style={{fontSize:11, textTransform:"uppercase", letterSpacing:0.6, color:"var(--ach-text-2)"}}>New temporary password</div>
            <div style={{fontFamily:"monospace", fontSize:18, fontWeight:700, color:"var(--ach-maroon)", marginTop:4}} data-testid="reset-pw-value">{result.temp_password}</div>
            <div style={{fontSize:11, color:"var(--ach-text-2)", marginTop:6}}>Only shown once. Copy it now, then hand it to them directly.</div>
          </div>
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--primary" onClick={onClose} data-testid="reset-pw-done">Done</button>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const [users, setUsers] = useState([]);
  const [conns, setConns] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [newCat, setNewCat] = useState({ category: "", sub_category: "", description: "", default_weekly_rate: "" });
  const [catBusy, setCatBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null); // {kind, target}
  const [resetResult, setResetResult] = useState(null);
  const [flash, setFlash] = useState(null);
  const [wipePhrase, setWipePhrase] = useState("");
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [wiping, setWiping] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const load = () => Promise.all([api.get("/settings/users"), api.get("/connections"), api.get("/catalogue")]).then(([u, c, cat]) => {
    setUsers(u.data); setConns(c.data.connections); setCatalogue(cat.data.items || []);
  });
  useEffect(() => { load(); }, []);

  const addCatItem = async () => {
    if (!newCat.category.trim() || !newCat.description.trim()) return;
    setCatBusy(true);
    try {
      const payload = { ...newCat };
      if (payload.default_weekly_rate) payload.default_weekly_rate = Number(payload.default_weekly_rate); else delete payload.default_weekly_rate;
      await api.post("/catalogue", payload);
      setNewCat({ category: "", sub_category: "", description: "", default_weekly_rate: "" });
      load();
    } catch (e) { showFlash("red", formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCatBusy(false); }
  };
  const removeCatItem = async (id) => {
    if (!window.confirm("Remove this catalogue item?")) return;
    await api.delete(`/catalogue/${id}`);
    load();
  };

  const isDirector = user?.role === "Director";
  const activeCount = users.filter(u => u.active !== false).length;
  const inactiveCount = users.length - activeCount;

  const showFlash = (tone, text) => { setFlash({tone, text}); setTimeout(()=>setFlash(null), 6000); };

  const doWipe = async () => {
    setConfirmWipe(false); setWiping(true);
    try {
      const r = await api.post("/dev/wipe-for-launch", { confirmation: "DELETE EVERYTHING" });
      showFlash("green", r.data.message + " Redirecting to Dashboard…");
      setWipePhrase("");
      setTimeout(() => nav("/dashboard"), 1200);
    } catch (e) { showFlash("red", formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setWiping(false); }
  };

  const doToggleActive = async (u) => {
    setConfirm(null);
    try {
      await api.patch(`/settings/users/${u.id}`, { active: u.active === false });
      showFlash("green", u.active === false ? `${u.name} can sign in again.` : `${u.name} has been turned off. Their history is kept.`);
      await load();
    } catch (e) { showFlash("red", formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const doResetPassword = async (u) => {
    setConfirm(null);
    try {
      const r = await api.post(`/settings/users/${u.id}/reset-password`);
      setResetResult(r.data);
      await load();
    } catch (e) { showFlash("red", formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const connectedCount = conns.filter(c => c.state.status === "connected").length;
  const liveCount = conns.filter(c => c.state.live_mode).length;
  const problemCount = conns.filter(c => c.state.status === "problem").length;

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Settings"
        subtitle="People, connections and everything else"
        actions={<button className="ach-btn ach-btn--ghost" onClick={logout} data-testid="btn-sign-out">Sign out</button>}
      />

      <div className="ach-card" style={{marginBottom: 16}}>
        <h2 className="ach-card__title">Signed in as</h2>
        <table className="ach-table">
          <tbody>
            <tr><td style={{color:"var(--ach-text-2)", width: 180}}>Name</td><td>{user?.name}</td></tr>
            <tr><td style={{color:"var(--ach-text-2)"}}>Email</td><td>{user?.email}</td></tr>
            <tr><td style={{color:"var(--ach-text-2)"}}>What you can do</td><td><StatusPill tone={ROLE_TONE[user?.role]}>{user?.role?.toUpperCase()}</StatusPill> <span style={{fontSize:12, color:"var(--ach-text-2)", marginLeft:8}}>{ROLE_DESC[user?.role]}</span></td></tr>
          </tbody>
        </table>
      </div>

      <div className="ach-card" style={{marginBottom: 16, cursor: "pointer"}} onClick={()=>nav("/connections")} data-testid="connections-link-card">
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:16}}>
          <div>
            <h2 className="ach-card__title" style={{margin: 0}}>Connections to your software</h2>
            <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:6}}>
              Hook the system up to Xero, Resend, Accessii and online signing. Connect first (safe — still logs), then switch on when you&apos;re ready.
            </div>
            <div style={{marginTop: 10, display:"flex", gap:8, flexWrap:"wrap"}}>
              <StatusPill tone="green">{connectedCount} CONNECTED</StatusPill>
              {liveCount > 0 && <StatusPill tone="maroon">{liveCount} SWITCHED ON</StatusPill>}
              {problemCount > 0 && <StatusPill tone="red">{problemCount} NEEDS A LOOK</StatusPill>}
              {connectedCount === 0 && problemCount === 0 && <StatusPill tone="neutral">NONE CONNECTED YET</StatusPill>}
            </div>
          </div>
          <button className="ach-btn ach-btn--primary" onClick={(e)=>{e.stopPropagation(); nav("/connections");}} data-testid="btn-open-connections">Open connections →</button>
        </div>
      </div>

      <div className="ach-card" style={{marginBottom: 16, cursor: "pointer"}} onClick={()=>nav("/settings/templates")} data-testid="templates-link-card">
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:16}}>
          <div>
            <h2 className="ach-card__title" style={{margin: 0}}>Email wording &amp; hire conditions</h2>
            <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:6}}>
              Edit the standard conditions of hire (used on every quote PDF) and the three payment reminder emails. No developer needed.
            </div>
          </div>
          <button className="ach-btn ach-btn--primary" onClick={(e)=>{e.stopPropagation(); nav("/settings/templates");}} data-testid="btn-open-templates">Open editor →</button>
        </div>
      </div>

      {isDirector && (
        <div className="ach-card" style={{marginBottom: 16, cursor: "pointer"}} onClick={()=>nav("/settings/bulk-import")} data-testid="bulk-import-link-card">
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:16}}>
            <div>
              <h2 className="ach-card__title" style={{margin: 0}}>Bulk import machines</h2>
              <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:6}}>
                Bring in the whole fleet from a spreadsheet in one go — useful for the initial Viberent migration or any time you&apos;re adding a batch of machines at once. Upload a CSV, review the preview, then import.
              </div>
            </div>
            <button className="ach-btn ach-btn--primary" onClick={(e)=>{e.stopPropagation(); nav("/settings/bulk-import");}} data-testid="btn-open-bulk-import">Open importer →</button>
          </div>
        </div>
      )}

      {isDirector && (
        <div className="ach-card" style={{marginBottom: 16, cursor: "pointer"}} onClick={()=>nav("/settings/bulk-import-hires")} data-testid="bulk-import-hires-link-card">
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:16}}>
            <div>
              <h2 className="ach-card__title" style={{margin: 0}}>Bulk import hires</h2>
              <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:6}}>
                For onboarding the fleet mid-flight — machine, customer, dates and weekly rate on one row. Missing customers or machines are created for you. Preview screen shows exactly what&apos;s about to happen.
              </div>
            </div>
            <button className="ach-btn ach-btn--primary" onClick={(e)=>{e.stopPropagation(); nav("/settings/bulk-import-hires");}} data-testid="btn-open-bulk-import-hires">Open importer →</button>
          </div>
        </div>
      )}

      {isDirector && <DashboardVisibilityCard />}

      <div className="ach-card">
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
          <div>
            <h2 className="ach-card__title" style={{margin:0}}>People with an account</h2>
            <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:4}}>
              {activeCount} active · {inactiveCount} turned off · {isDirector ? "Only Directors can add or change people." : "Only a Director can add or change people."}
            </div>
          </div>
          {isDirector && (
            <button className="ach-btn ach-btn--primary" onClick={()=>setAddOpen(true)} data-testid="btn-add-person">+ Add a person</button>
          )}
        </div>
        <table className="ach-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>What they can do</th>
              <th>Status</th>
              {isDirector && <th></th>}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} style={u.active === false ? {opacity:0.55} : undefined}>
                <td>{u.name}{u.id === user?.id && <span style={{fontSize:11, color:"var(--ach-text-2)", marginLeft:6}}>(you)</span>}</td>
                <td>{u.email}</td>
                <td><StatusPill tone={ROLE_TONE[u.role]}>{u.role.toUpperCase()}</StatusPill></td>
                <td>{u.active === false
                  ? <StatusPill tone="red">TURNED OFF</StatusPill>
                  : <StatusPill tone="green">ACTIVE</StatusPill>}
                </td>
                {isDirector && (
                  <td style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                    {u.active !== false && (
                      <button className="ach-btn ach-btn--grey" style={{padding:"4px 8px", fontSize:11}} onClick={()=>setEditing(u)} data-testid={`edit-user-${u.id}`}>Change role</button>
                    )}
                    {u.active !== false && (
                      <button className="ach-btn ach-btn--grey" style={{padding:"4px 8px", fontSize:11}}
                        onClick={()=>setConfirm({kind:"reset", target:u})} data-testid={`reset-pw-${u.id}`}>Reset password</button>
                    )}
                    <button className="ach-btn ach-btn--ghost" style={{padding:"4px 8px", fontSize:11, color:u.active === false ? "var(--ach-green)" : "var(--ach-red)"}}
                      onClick={()=>setConfirm({kind: u.active === false ? "activate" : "deactivate", target:u})} data-testid={`toggle-active-${u.id}`}>
                      {u.active === false ? "Turn back on" : "Turn off"}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{fontSize: 12, color:"var(--ach-text-2)", marginTop: 10}}>
          Turning someone off keeps their history but stops them signing in. You can always turn them back on later.
        </div>
      </div>

      {addOpen && <AddPersonDialog onClose={()=>setAddOpen(false)} onSaved={load} />}
      {editing && <EditPersonDialog target={editing} currentUser={user} onClose={()=>setEditing(null)} onSaved={load} />}

      <div className="ach-card" style={{marginTop:16}} data-testid="catalogue-card">
        <h2 className="ach-card__title">Quote cranes <small>gear you can quote but don&apos;t own or have cross-hired yet</small></h2>
        <p style={{fontSize:12, color:"var(--ach-text-2)", margin:"4px 0 12px", lineHeight:1.5}}>
          Add categories and sub-categories for gear you want to quote on but don&apos;t actually have — no real unit, no supplier locked in yet.
          These <strong>don&apos;t</strong> appear in the fleet or fleet KPIs — they only show up as options on the New Quote form. (Once you&apos;ve actually got a real cross-hired machine on a job, that&apos;s tracked separately under Cross hire.)
        </p>
        <div className="ach-form-grid" style={{gridTemplateColumns:"1.2fr 1.2fr 1.6fr 1fr auto", gap:8, alignItems:"end"}}>
          <div className="ach-field"><label>Category</label>
            <input value={newCat.category} onChange={(e)=>setNewCat({...newCat, category:e.target.value})} data-testid="cat-new-category" placeholder="e.g. Slew crane" />
          </div>
          <div className="ach-field"><label>Sub-category</label>
            <input value={newCat.sub_category} onChange={(e)=>setNewCat({...newCat, sub_category:e.target.value})} data-testid="cat-new-subcat" placeholder="e.g. 50t city" />
          </div>
          <div className="ach-field"><label>Description</label>
            <input value={newCat.description} onChange={(e)=>setNewCat({...newCat, description:e.target.value})} data-testid="cat-new-desc" placeholder="e.g. 50t Franna city crane" />
          </div>
          <div className="ach-field"><label>Default weekly $</label>
            <input type="number" value={newCat.default_weekly_rate} onChange={(e)=>setNewCat({...newCat, default_weekly_rate:e.target.value})} data-testid="cat-new-rate" />
          </div>
          <button className="ach-btn ach-btn--primary" style={{padding:"8px 16px"}} onClick={addCatItem} disabled={catBusy || !newCat.category.trim() || !newCat.description.trim()} data-testid="cat-add">+ Add</button>
        </div>
        {catalogue.length > 0 && (
          <table className="ach-table" style={{marginTop:14}}>
            <thead><tr><th>Category</th><th>Sub-category</th><th>Description</th><th>Default weekly</th><th></th></tr></thead>
            <tbody>
              {catalogue.map(item => (
                <tr key={item.id} data-testid={`cat-row-${item.id}`}>
                  <td>{item.category}</td>
                  <td>{item.sub_category || "—"}</td>
                  <td>{item.description}</td>
                  <td>{item.default_weekly_rate ? `$${item.default_weekly_rate.toLocaleString()}` : "—"}</td>
                  <td>{isDirector && <button className="ach-btn ach-btn--ghost" style={{padding:"4px 8px", fontSize:11, color:"var(--ach-red)"}} onClick={()=>removeCatItem(item.id)} data-testid={`cat-remove-${item.id}`}>Remove</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isDirector && (
        <div className="ach-card" data-testid="wipe-launch-card" style={{marginTop:16, borderLeft:"4px solid var(--ach-red)"}}>
          <h2 className="ach-card__title" style={{color:"var(--ach-red)"}}>Wipe the system for go-live</h2>
          <p style={{fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5, margin:"6px 0 12px"}}>
            Use this ONCE, when you&apos;re ready to switch from demo data to the real fleet and real customers.
            It permanently deletes every machine, customer, hire, quote, month-end run, adjustment, invoice and
            activity-log entry from the database. You then import the real data via the CSV importer or add it by hand.
          </p>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14}}>
            <div style={{padding:10, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12, color:"var(--ach-text-1)"}}>
              <div style={{fontWeight:700, color:"var(--ach-red)", marginBottom:4}}>WILL BE DELETED</div>
              Machines · Customers · Hires (active + finished) · Quotes · Transport records · Month-end runs · Locked-month adjustments · Company invoices &amp; money-owed · Activity/emails log
            </div>
            <div style={{padding:10, background:"var(--ach-green-tint)", borderRadius:6, fontSize:12, color:"var(--ach-text-1)"}}>
              <div style={{fontWeight:700, color:"var(--ach-green)", marginBottom:4}}>WILL BE KEPT</div>
              All {users.length} user accounts (Peter, Todd, Linda, Mark, Read Only, plus anyone you&apos;ve added) · Email wording &amp; hire-conditions templates · Connections/integrations config
            </div>
          </div>
          <div className="ach-field" style={{maxWidth:400, marginBottom:12}}>
            <label style={{color:"var(--ach-red)", fontWeight:600}}>Type <span style={{fontFamily:"monospace", background:"var(--ach-red-tint)", padding:"0 6px", borderRadius:4}}>DELETE EVERYTHING</span> to confirm</label>
            <input value={wipePhrase} onChange={(e)=>setWipePhrase(e.target.value)}
              placeholder="DELETE EVERYTHING" data-testid="wipe-phrase"
              style={{borderColor: wipePhrase && wipePhrase !== "DELETE EVERYTHING" ? "var(--ach-red)" : undefined}} />
            <div className="helper" style={{color:"var(--ach-text-2)"}}>
              This is intentional friction — nobody should be able to do this by accident.
            </div>
          </div>
          <button className="ach-btn ach-btn--primary"
            style={{background: wipePhrase === "DELETE EVERYTHING" ? "var(--ach-red)" : undefined}}
            onClick={()=>setConfirmWipe(true)}
            disabled={wipePhrase !== "DELETE EVERYTHING" || wiping}
            data-testid="btn-wipe-launch">
            {wiping ? "Wiping…" : "Wipe the system now"}
          </button>
        </div>
      )}

      {confirmWipe && (
        <div className="ach-modal-backdrop" data-testid="confirm-wipe-dialog" onClick={()=>setConfirmWipe(false)}>
          <div className="ach-modal" style={{maxWidth: 520}} onClick={(e)=>e.stopPropagation()}>
            <div className="ach-modal__header"><h2 style={{margin:0,fontSize:16,fontWeight:600,color:"var(--ach-red)"}}>Really wipe everything?</h2></div>
            <div className="ach-modal__body">
              <p style={{margin:"0 0 10px", fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>
                Last chance. There&apos;s no undo — the demo data can be reset back later from the dev-endpoint if you need it, but the current business data (if any) is gone.
              </p>
              <p style={{margin:0, fontSize:12, color:"var(--ach-text-2)"}}>
                A single entry will be left in the activity log showing that you did this and when — so there&apos;s a permanent record.
              </p>
            </div>
            <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
              <button className="ach-btn ach-btn--grey" onClick={()=>setConfirmWipe(false)} data-testid="confirm-wipe-cancel">Cancel — keep everything</button>
              <button className="ach-btn ach-btn--primary" style={{background:"var(--ach-red)"}}
                onClick={doWipe} data-testid="confirm-wipe-go">Yes, wipe it all</button>
            </div>
          </div>
        </div>
      )}

      {confirm?.kind === "deactivate" && (
        <ConfirmActionDialog testid="confirm-deactivate" title={`Turn ${confirm.target.name} off?`}
          body={`They won't be able to sign in until a Director turns them back on. Their history, activity log entries and anything they've done stays exactly as it was.`}
          confirmLabel="Yes, turn them off" danger
          onCancel={()=>setConfirm(null)} onConfirm={()=>doToggleActive(confirm.target)} />
      )}
      {confirm?.kind === "activate" && (
        <ConfirmActionDialog testid="confirm-activate" title={`Turn ${confirm.target.name} back on?`}
          body={`They'll be able to sign in with their old password. If they can't remember it, use Reset password afterwards.`}
          confirmLabel="Yes, turn them back on"
          onCancel={()=>setConfirm(null)} onConfirm={()=>doToggleActive(confirm.target)} />
      )}
      {confirm?.kind === "reset" && (
        <ConfirmActionDialog testid="confirm-reset-pw" title={`Reset password for ${confirm.target.name}?`}
          body={`We'll generate a fresh temporary password and show it to you once. Their old password stops working immediately.`}
          confirmLabel="Yes, reset it"
          onCancel={()=>setConfirm(null)} onConfirm={()=>doResetPassword(confirm.target)} />
      )}
      {resetResult && <ResetPasswordResultDialog result={resetResult} onClose={()=>setResetResult(null)} />}

      {flash && (
        <div data-testid="settings-flash"
          style={{position:"fixed", right:20, bottom:20, zIndex:1000, padding:"10px 14px", borderRadius:8,
            background: flash.tone === "red" ? "var(--ach-red-tint)" : "var(--ach-green-tint)",
            color: flash.tone === "red" ? "var(--ach-red)" : "var(--ach-green)",
            border:"1px solid var(--ach-card-border)", fontSize:13, fontWeight:500, maxWidth:360, boxShadow:"0 4px 16px rgba(0,0,0,0.08)"}}>
          {flash.text}
        </div>
      )}
    </div>
  );
}
